// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

export enum ProductType {
  Consumable = "consumable",
  NonConsumable = "non_consumable",
  Subscription = "subscription"
}

export enum RewardType {
  Coin = 1,
  LudoCoin = 2,
  QuizCoin = 3,
  SolitaireCoin = 4
}

export interface ProductReward {
  type: RewardType;
  amount: number;
}

export interface ShopProduct {
  productID: string;
  type: ProductType;
  iosID: string;
  googleID: string;
  enabled: boolean;
  priority: number;
  rewards: ProductReward[];
  json_config: Record<string, any> | null;
}

export interface ShopConfiguration {
  products: Record<string, ShopProduct>;
}

// Helper functions for enum display
export const getProductTypeName = (type: ProductType): string => {
  switch (type) {
    case ProductType.Consumable:
      return "Consumable";
    case ProductType.NonConsumable:
      return "Non-Consumable";
    case ProductType.Subscription:
      return "Subscription";
    default:
      return type;
  }
};

export const getRewardTypeName = (type: RewardType): string => {
  switch (type) {
    case RewardType.Coin:
      return "Coin";
    case RewardType.LudoCoin:
      return "Ludo Coin";
    case RewardType.QuizCoin:
      return "Quiz Coin";
    case RewardType.SolitaireCoin:
      return "Solitaire Coin";
    default:
      return `Type ${type}`;
  }
};

// Validation functions
export const isValidProductType = (type: string): type is ProductType => {
  return Object.values(ProductType).includes(type as ProductType);
};

export const isValidRewardType = (type: number): type is RewardType => {
  return Object.values(RewardType).includes(type as RewardType);
};

// Default configuration generator
export const createDefaultProduct = (productID: string): ShopProduct => {
  return {
    productID,
    type: ProductType.Consumable,
    iosID: `com.investaplay.investaplay.${productID}`,
    googleID: `com.investaplay.games.${productID}`,
    enabled: true,
    priority: 0,
    rewards: [
      {
        type: RewardType.Coin,
        amount: 100
      }
    ],
    json_config: null
  };
};

// Product validation
export const validateProduct = (product: ShopProduct): string[] => {
  const errors: string[] = [];

  if (!product.productID || product.productID.trim() === '') {
    errors.push('Product ID is required');
  }

  if (!isValidProductType(product.type)) {
    errors.push('Invalid product type');
  }

  if (!product.iosID || product.iosID.trim() === '') {
    errors.push('iOS ID is required');
  }

  if (!product.googleID || product.googleID.trim() === '') {
    errors.push('Google Play ID is required');
  }

  if (product.priority < 0) {
    errors.push('Priority must be non-negative');
  }

  if (!product.rewards || product.rewards.length === 0) {
    errors.push('At least one reward is required');
  } else {
    product.rewards.forEach((reward, index) => {
      if (!isValidRewardType(reward.type)) {
        errors.push(`Invalid reward type for reward ${index + 1}`);
      }
      if (reward.amount <= 0) {
        errors.push(`Reward amount must be positive for reward ${index + 1}`);
      }
    });
  }

  return errors;
};

// Shop configuration validation
export const validateShopConfiguration = (config: ShopConfiguration): string[] => {
  const errors: string[] = [];

  if (!config.products || Object.keys(config.products).length === 0) {
    errors.push('No products configured');
    return errors;
  }

  Object.entries(config.products).forEach(([productKey, product]) => {
    const productErrors = validateProduct(product);
    productErrors.forEach(error => {
      errors.push(`${productKey}: ${error}`);
    });
  });

  return errors;
};
